const DB_NAME = "twlc-ui-packages";
const DB_VERSION = 1;
const PACKAGE_STORE = "packages";
const permissionCache = {};
const packageCache = {};
const packageRequestCache = {};
const packageRefreshState = {};
const PACKAGE_REFRESH_INTERVAL_MS = 60000;

function canonicalStringify(value) {
  if (Array.isArray(value)) {
    return `[${value.map(canonicalStringify).join(",")}]`;
  }

  if (value && typeof value === "object") {
    return `{${Object.keys(value).sort().map((key) => {
      return `${JSON.stringify(key)}:${canonicalStringify(value[key])}`;
    }).join(",")}}`;
  }

  return JSON.stringify(value);
}

function openPackageDb() {
  return new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) {
      reject(new Error("IndexedDB is not available"));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(PACKAGE_STORE)) {
        db.createObjectStore(PACKAGE_STORE, { keyPath: "app" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function readPackage(app) {
  const db = await openPackageDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PACKAGE_STORE, "readonly");
    const request = tx.objectStore(PACKAGE_STORE).get(app);
    request.onsuccess = () => resolve(request.result ? request.result.package : null);
    request.onerror = () => reject(request.error);
  });
}

async function writePackage(app, uiPackage) {
  const db = await openPackageDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PACKAGE_STORE, "readwrite");
    tx.objectStore(PACKAGE_STORE).put({
      app,
      package: uiPackage,
      saved_at: Date.now(),
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function base64UrlToArrayBuffer(value) {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const base64 = `${value}${padding}`.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
}

function pemToArrayBuffer(pem) {
  const base64 = pem
    .replace("-----BEGIN PUBLIC KEY-----", "")
    .replace("-----END PUBLIC KEY-----", "")
    .replace(/\s/g, "");
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
}

async function verifyPackageSignature(uiPackage) {
  if (!uiPackage || !uiPackage.signature) {
    return false;
  }

  if (uiPackage.signature.alg === "none") {
    console.warn("UI package is unsigned. Configure TWLC_UI_PACKAGE_PRIVATE_KEY_PATH for tamper detection.");
    return true;
  }

  if (
    uiPackage.signature.alg !== "RSA-PSS-SHA256" ||
    !uiPackage.signature.value ||
    !uiPackage.signature.public_key ||
    !window.crypto ||
    !window.crypto.subtle
  ) {
    return false;
  }

  const payload = { ...uiPackage };
  delete payload.signature;

  const key = await window.crypto.subtle.importKey(
    "spki",
    pemToArrayBuffer(uiPackage.signature.public_key),
    {
      name: "RSA-PSS",
      hash: "SHA-256",
    },
    false,
    ["verify"]
  );

  const encodedPayload = new TextEncoder().encode(canonicalStringify(payload));
  return window.crypto.subtle.verify(
    {
      name: "RSA-PSS",
      saltLength: 32,
    },
    key,
    base64UrlToArrayBuffer(uiPackage.signature.value),
    encodedPayload
  );
}

async function fetchAndStorePackage(app) {
  if (packageRequestCache[app]) {
    return packageRequestCache[app];
  }

  packageRequestCache[app] = doFetchAndStorePackage(app).finally(() => {
    delete packageRequestCache[app];
  });

  return packageRequestCache[app];
}

async function doFetchAndStorePackage(app) {
  const response = await fetch(`/ui-package/${app}`, { credentials: "include" });
  if (!response.ok) {
    throw new Error(`Could not load UI package for ${app}`);
  }

  const uiPackage = await response.json();
  const verified = await verifyPackageSignature(uiPackage);
  if (!verified) {
    throw new Error(`UI package signature verification failed for ${app}`);
  }

  packageCache[app] = uiPackage;
  await writePackage(app, uiPackage);
  return uiPackage;
}

async function fetchPackageManifest(app) {
  const response = await fetch(`/ui-package/${app}/manifest`, { credentials: "include" });
  if (!response.ok) {
    return null;
  }
  return response.json();
}

function getPackageVersion(uiPackage) {
  return uiPackage ? uiPackage.package_version || uiPackage.manifest_version : null;
}

function refreshPackageInBackground(app, currentPackage) {
  const lastRefresh = packageRefreshState[app] || 0;
  if (Date.now() - lastRefresh < PACKAGE_REFRESH_INTERVAL_MS) {
    return;
  }

  packageRefreshState[app] = Date.now();
  fetchPackageManifest(app).then((manifest) => {
    if (!manifest || manifest.package_version !== getPackageVersion(currentPackage)) {
      return fetchAndStorePackage(app);
    }
    return null;
  }).catch(() => {});
}

export async function getUiPackage(app) {
  if (packageCache[app]) {
    refreshPackageInBackground(app, packageCache[app]);
    return packageCache[app];
  }

  const storedPackage = await readPackage(app).catch(() => null);
  if (storedPackage && await verifyPackageSignature(storedPackage)) {
    packageCache[app] = storedPackage;
    refreshPackageInBackground(app, storedPackage);
    return storedPackage;
  }

  return fetchAndStorePackage(app);
}

async function fetchPermissions(app) {
  const response = await fetch(`/ui-package/${app}/permissions`, { credentials: "include" });
  if (!response.ok) {
    return { allowed_scopes: [] };
  }
  return response.json();
}

async function canRenderComponent(app, componentEntry) {
  if (!componentEntry || !componentEntry.access || componentEntry.access.public) {
    return true;
  }

  if (!navigator.onLine) {
    return false;
  }

  if (!permissionCache[app]) {
    permissionCache[app] = fetchPermissions(app).catch(() => ({ allowed_scopes: [] }));
  }

  const permissions = await permissionCache[app];
  return (permissions.allowed_scopes || []).includes(componentEntry.access.scope);
}

export function redirectToAppLogin(app) {
  const loginPath = app ? `/${app}/login` : "/login";
  if (window.location.pathname !== loginPath) {
    window.location.href = loginPath;
  }
}

export async function loadComponentFromPackage(app, componentId) {
  const uiPackage = await getUiPackage(app);
  const componentEntry = uiPackage.components ? uiPackage.components[componentId] : null;
  if (!componentEntry) {
    return null;
  }

  const allowed = await canRenderComponent(app, componentEntry);
  if (!allowed) {
    return { notAuthorized: true };
  }

  return componentEntry.data;
}
